import { cn } from "@/lib/utils";
import { TAG_CATEGORIES, tagIconsByCategory } from "@caliu/styles/icons";
import { tagIconDisplayName, tagIconKeywords } from "@caliu/styles/icons/keywords";
import { searchIcons } from "@caliu/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TagIcon } from "../lib/tagIcons";
import { Icon } from "@/components/ui/Icon";

interface TagIconPickerProps {
  value: string | null;
  onChange: (key: string | null) => void;
}

const SECTIONS = TAG_CATEGORIES.map((category) => ({
  id: category.id,
  keys: tagIconsByCategory[category.id].map((icon) => icon.tag as string),
}));

/**
 * Tag-icon grid: six unlabelled sections (spacing + a hairline between them;
 * screen readers still get each section's name) and a search field that
 * matches icon names in every language, whatever the app language is.
 */
export function TagIconPicker({ value, onChange }: TagIconPickerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const [query, setQuery] = useState("");

  const results = useMemo(
    () => (query.trim() ? searchIcons(query, tagIconKeywords, locale) : null),
    [query, locale],
  );

  const toggle = (key: string) => onChange(value === key ? null : key);

  const grid = (keys: string[]) => (
    <div className="grid grid-cols-7 gap-1">
      {keys.map((key) => {
        const name = tagIconDisplayName(key, locale) ?? key;
        const selected = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => toggle(key)}
            title={name}
            aria-label={name}
            aria-pressed={selected}
            className={cn(
              "flex h-10 w-full items-center justify-center rounded-md transition-colors duration-100",
              selected
                ? "bg-accent-bg outline outline-2 -outline-offset-1 outline-accent text-accent"
                : "text-ink-secondary hover:bg-hover hover:text-ink",
            )}
          >
            <TagIcon name={key} size={24} />
          </button>
        );
      })}
    </div>
  );

  return (
    <div>
      <div className="relative mb-2">
        <Icon
          name="search"
          size={14}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
        />
        <input
          type="text"
          enterKeyHint="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && results?.[0]) {
              e.preventDefault();
              toggle(results[0]);
            }
          }}
          placeholder={t("tagEditor.searchIcons")}
          aria-label={t("tagEditor.searchIcons")}
          className="h-8 w-full rounded-full border border-border-light bg-transparent pl-8 pr-3 text-sm text-ink placeholder:text-placeholder outline-none focus-visible:border-accent"
        />
      </div>
      <div className="h-64 overflow-y-auto overscroll-contain pr-0.5">
        {results ? (
          results.length > 0 ? (
            grid(results)
          ) : (
            <p className="py-6 text-center text-xs text-muted">{t("tagEditor.noIcons")}</p>
          )
        ) : (
          SECTIONS.map((section, index) => (
            <div
              key={section.id}
              role="group"
              aria-label={t(`tagEditor.categories.${section.id}`)}
              className={cn(index > 0 && "mt-2 border-t border-border-light pt-2")}
            >
              {grid(section.keys)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
